/**
 * Position du téléphone pendant le travail (décision du propriétaire du
 * 16 octobre 2026).
 *
 * - Seuls les membres qui vont sur le terrain (technicien, ou patron/bureau qui
 *   intervient aussi) sont concernés, et seulement si l'entreprise utilise la
 *   position (étapes relevées, heure d'arrivée au client, technicien le plus proche).
 * - L'application explique d'abord pourquoi, puis déclenche la demande du
 *   téléphone ou du navigateur. Un refus est respecté : plus aucune relance, la
 *   proposition reste dans le Menu.
 * - Hors d'une demande expresse, la position n'est JAMAIS lue sans autorisation
 *   déjà donnée : aucune fenêtre du système ne surgit au milieu du travail.
 *
 * Le choix fait sur cet appareil est gardé dans le navigateur, par compte : ni
 * position ni coordonnée n'y est écrite.
 */
import { isFieldWorker, type Me } from './auth.js';

export type LocationPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

export interface LocationFeatures {
  eta: boolean;
  nearest: boolean;
  steps: boolean;
}

/** Usages de la position choisis par l'entreprise, lus dans la session. */
export function locationFeatures(me: Me | null | undefined): LocationFeatures {
  return {
    eta: me?.location_features?.eta === true,
    nearest: me?.location_features?.nearest === true,
    steps: me?.location_features?.steps === true,
  };
}

/** Ce compte va sur le terrain et l'entreprise utilise sa position pour au moins un usage. */
export function locationConcerns(me: Me | null | undefined): boolean {
  if (!me || me.support_session) return false;
  if (!isFieldWorker(me) && !me.field_operator) return false;
  const features = locationFeatures(me);
  return features.eta || features.nearest || features.steps;
}

/**
 * Faut-il lire la position au départ pour l'heure d'arrivée ? Seulement pour son
 * propre départ, quand on va sur le terrain et que l'entreprise annonce l'heure :
 * le bureau qui fait partir un autre membre n'envoie jamais sa propre position.
 * Le serveur applique la même règle.
 */
export function etaPositionWanted(
  me: Me | null | undefined, to: string, etaSharing: boolean | undefined, assigneeId: string | null | undefined,
): boolean {
  return to === 'en_route' && etaSharing === true && Boolean(me) && assigneeId === me!.id && locationConcerns(me);
}

/** Choix fait sur cet appareil : « asked » (demande faite), « later » (plus tard), « seen » (déjà lue). */
type DeviceChoice = 'asked' | 'later' | 'seen';
const choiceKey = (userId: string): string => `myequip.position.${userId}`;

export function deviceChoice(userId: string): DeviceChoice | null {
  try {
    const value = localStorage.getItem(choiceKey(userId));
    return value === 'asked' || value === 'later' || value === 'seen' ? value : null;
  } catch {
    return null;
  }
}

export function rememberDeviceChoice(userId: string, choice: DeviceChoice): void {
  try {
    localStorage.setItem(choiceKey(userId), choice);
  } catch {
    // Stockage indisponible (navigation privée) : la question pourra revenir.
  }
}

/** État de l'autorisation sans jamais la demander. */
export async function locationPermission(): Promise<LocationPermission> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return 'unsupported';
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    if (status) return status.state as LocationPermission;
  } catch {
    // Coque native ou navigateur sans API d'autorisations.
  }
  return 'prompt';
}

export interface DevicePosition {
  latitude: number;
  longitude: number;
}

/**
 * Lit la position une fois. Avec `ask`, le téléphone peut afficher sa demande
 * d'autorisation ; sinon l'appelant a vérifié qu'elle est déjà donnée. Ne bloque
 * jamais plus de `guardMs` : une demande restée sans réponse ne retient rien.
 */
export function readPosition(guardMs = 5000): Promise<DevicePosition | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    const guard = window.setTimeout(() => resolve(null), guardMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(guard);
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      () => {
        window.clearTimeout(guard);
        resolve(null);
      },
      // Précision réduite : un repère à ~100 m suffit, et le téléphone consomme moins.
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: Math.max(1000, guardMs - 1000) },
    );
  });
}

/** Déclenche la demande du système après l'explication ; renvoie l'état obtenu. */
export async function askLocation(userId: string): Promise<LocationPermission> {
  rememberDeviceChoice(userId, 'asked');
  // Laisser à la personne le temps de lire la demande du système.
  const position = await readPosition(30_000);
  if (position) rememberDeviceChoice(userId, 'seen');
  const state = await locationPermission();
  return position ? 'granted' : state === 'granted' ? 'prompt' : state;
}

/**
 * La position peut-elle être lue SANS faire surgir de demande ? Oui si
 * l'autorisation est donnée ; sans API d'autorisations (coque native), si une
 * lecture a déjà réussi sur cet appareil.
 */
export async function locationReadable(userId: string): Promise<boolean> {
  const state = await locationPermission();
  if (state === 'granted') return true;
  if (state === 'denied' || state === 'unsupported') return false;
  return typeof navigator.permissions?.query !== 'function' && deviceChoice(userId) === 'seen';
}

/** Texte d'explication commun à l'écran de première fois et au Menu. */
export const LOCATION_PURPOSE =
  'Pour prévenir le client de votre heure d’arrivée et aider le bureau en cas d’urgence. '
  + 'Votre position n’est jamais envoyée au client ni gardée en historique.';
